import Player from '@vimeo/player';

/**
 * PDP Video class - specifically for PDP custom banner video controls
 * Handles audio detection and mute button state management
 */
export default class PDPVideo {
  /**
   * @param {DOMnode} element top-level node for the section/component
   * @param {DOMnode} video top-level node for the video
   * @param {Class} layout for handling breakpoints
   */
  constructor({ element, video, layout }) {
    const dataEl = video.querySelector('[data-component-data]');
    
    this.element = element;
    this.layout = layout;
    this.data = dataEl ? JSON.parse(dataEl.textContent) : {};
    
    this.isMuted = true;
    this.isMobile = true;
    this.isPlaying = this.data.autoplay;
    this.hasMultipleVideos = !!this.data.videoDesktop && !!this.data.videoMobile;

    this.handlePlay = this.handlePlay.bind(this);
    this.handleMute = this.handleMute.bind(this);
    this.handleBpChange = this.handleBpChange.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.playEl = this.data.play.selector
      ? element.querySelector(this.data.play.selector)
      : video.querySelector('[data-video-play]');
    this.muteEl = this.data.mute.selector
      ? element.querySelector(this.data.mute.selector)
      : video.querySelector('[data-video-mute]');

    // Disable mute button by default - will be enabled if audio is detected
    if (this.muteEl) {
      this.disableMuteButton();
    }

    // Set initial button states
    this.updatePlayButtonIcons();
    this.updateMuteButtonIcons();

    this.init();
  }

  handlePlay() {
    if (this.isPlaying) {
      this.pauseVideo();
    } else this.playVideo();
    
    // Update button icons to reflect current state
    this.updatePlayButtonIcons();
  }

  pauseVideo() {
    this.player.pause();
    this.isPlaying = false;
    this.playEl?.setAttribute('aria-label', this.data.play.playText);
  }

  playVideo() {
    this.player.play();
    this.isPlaying = true;
    this.playEl?.setAttribute('aria-label', this.data.play.pauseText);
    
    // Enable mute button when video starts playing
    // This ensures users can attempt to unmute even if audio detection fails
    if (this.muteEl) {
      this.enableMuteButton();
    }
  }

  handleMute() {
    if (this.isMuted) {
      this.unmuteVideo();
    } else {
      this.muteVideo();
    }

    // Update button icons to reflect current state
    this.updateMuteButtonIcons();
  }

  unmuteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = false;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(1);
    }
    this.isMuted = false;
    this.muteEl?.setAttribute('aria-label', this.data.mute.muteText);
  }

  muteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = true;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(0);
    }
    this.isMuted = true;
    this.muteEl?.setAttribute('aria-label', this.data.mute.unmuteText);
  }

  resetPlayer() {
    this.unbindEventListeners();

    if (this.data.host === 'shopify') {
      this.resetShopifyPlayer();
    } else if (this.data.host === 'vimeo') {
      this.resetVimeoPlayer();
    }
  }

  unbindEventListeners() {
    this.playEl?.removeEventListener('click', this.handlePlay);
    this.muteEl?.removeEventListener('click', this.handleMute);
    if (this.layout) {
      window.removeEventListener('bpChange', this.handleBpChange);
      window.removeEventListener('resize', this.handleResize);
    }
  }

  resetShopifyPlayer() {
    this.resetControlButtons();
  }

  resetVimeoPlayer() {
    this.player.destroy().then(() => {
      this.resetControlButtons();
      this.init();
    });
  }

  resetControlButtons() {
    this.isPlaying = this.data.autoplay;
    this.updatePlayButtonIcons();

    this.isMuted = true;
    this.updateMuteButtonIcons();
  }

  updatePlayButtonIcons() {
    if (!this.playEl) return;
    
    if (this.isPlaying) {
      // Video is playing - show pause icon (second child), hide play icon (first child)
      this.playEl.children[0]?.classList.add('hidden');    // play icon  
      this.playEl.children[1]?.classList.remove('hidden'); // pause icon
      this.playEl.setAttribute('aria-label', this.data.play.pauseText);
    } else {
      // Video is paused - show play icon (first child), hide pause icon (second child)
      this.playEl.children[0]?.classList.remove('hidden'); // play icon
      this.playEl.children[1]?.classList.add('hidden');    // pause icon
      this.playEl.setAttribute('aria-label', this.data.play.playText);
    }
  }

  updateMuteButtonIcons() {
    if (!this.muteEl) return;
    
    if (this.isMuted) {
      // Video is muted - show sound-off icon (first child), hide sound-on icon (second child)
      this.muteEl.children[0]?.classList.remove('hidden'); // sound-off icon
      this.muteEl.children[1]?.classList.add('hidden');    // sound-on icon
      this.muteEl.setAttribute('aria-label', this.data.mute.unmuteText);
    } else {
      // Video is unmuted - show sound-on icon (second child), hide sound-off icon (first child)
      this.muteEl.children[0]?.classList.add('hidden');    // sound-off icon
      this.muteEl.children[1]?.classList.remove('hidden'); // sound-on icon
      this.muteEl.setAttribute('aria-label', this.data.mute.muteText);
    }
  }

  handleBpChange() {
    if (this.isMobile && this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = false;
      this.resetPlayer();
    } else if (!this.isMobile && !this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  handleResize() {
    if (this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = false;
      this.resetPlayer();
    } else {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  init() {    
    if (this.layout && this.hasMultipleVideos) {
      if (this.layout.isAboveBreakpoint('sm')) {
        this.initVideoByType(this.data.videoDesktop);
        this.isMobile = false;
      } else {
        this.initVideoByType(this.data.videoMobile);
        this.isMobile = true;
      }
    } else if (this.data.videoDesktop) {
        this.initVideoByType(this.data.videoDesktop);
    } else if (this.data.videoMobile) {
      this.initVideoByType(this.data.videoMobile);
    }
  }

  initVideoByType(video) {    
    if (this.data.host === 'shopify') {
      this.initShopifyVideo(video.id);
    } else if (this.data.host === 'vimeo') {
      this.initVimeoVideo(video);
    }
  }

  onTimeUpdate = (vimeoPlayer) => {
    vimeoPlayer.getCurrentTime().then((currentTime) => {
      const roundedTime = Math.floor(currentTime);
      // Detect if the video is playing based on the roundedTime value
      if (roundedTime > 0) {
        // If the video is playing, add a class to the video container if its present
        const videoContainer = this.element.querySelector('.hero-secondary__video');
        if (videoContainer && !videoContainer.classList.contains('video-playing')) {
          videoContainer.classList.add('video-playing');
        }
      }
    });
  };

  initVimeoVideo(id) {
    const elementId = `video-${this.data.prefix}${id}`;
    
    const options = {
      id,
      controls: false,
      autoplay: this.data.autoplay,
      muted: true,
      responsive: true,
      loop: true,
    };

    this.player = new Player(elementId, options);      
    this.player.on('timeupdate', () => this.onTimeUpdate(this.player));
    this.bindEventListeners();
  }

  initShopifyVideo(id) {
    // Try first in direct selector (original way)
    this.player = this.element.querySelector(`#video-${id} video`);
    
    // If not found, try to find video element in any container with ID video-[id]
    if (!this.player) {
      const container = this.element.querySelector(`#video-${id}`);
      if (container) {
        this.player = container.querySelector('video');
      }
    }
    
    // Final fallback: try to find ANY video element in the component
    if (!this.player) {
      this.player = this.element.querySelector('video');
    }
    
    if (this.player) {
      // Ensure proper attributes for Safari mobile
      this.player.setAttribute('playsinline', true);
      this.player.setAttribute('webkit-playsinline', true);
      
      // Check for audio tracks and disable mute button if no sound
      if (this.player.readyState >= 1) {
        // Metadata is already loaded
        this.checkAudioTracks();
      } else {
        // Metadata not yet loaded, wait for event
        this.player.addEventListener('loadedmetadata', () => {
          this.checkAudioTracks();
        }, { once: true });
      }
      
      // For iOS Safari specifically
      if (this.data.autoplay) {
        const handleAutoplay = () => {
          // Delay play slightly to ensure Safari has fully loaded the video
          setTimeout(() => {
            const playPromise = this.player.play();
            
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // Auto-play was prevented, try once more with user interaction
                this.isPlaying = false;
              });
            }
          }, 100);
        };
        
        if (this.player.readyState >= 1) {
          handleAutoplay();
        } else {
          this.player.addEventListener('loadedmetadata', handleAutoplay, { once: true });
        }
      }
      
      this.bindEventListeners();
    }
  }

  checkAudioTracks() {
    if (!this.player || !this.muteEl) {
      return;
    }

    let hasAudio = false;

    // Method 1: Check audioTracks
    if (this.player.audioTracks && this.player.audioTracks.length > 0) {
      hasAudio = true;
    }

    // Method 2: Check mozHasAudio (Firefox)
    if (!hasAudio && typeof this.player.mozHasAudio === 'boolean') {
      hasAudio = this.player.mozHasAudio;
    }

    // Method 3: For webkit-based browsers, always use polling as webkitAudioDecodedByteCount
    // may not be populated immediately
    if (!hasAudio && 'webkitAudioDecodedByteCount' in this.player) {
      this.pollWebkitAudioDecodedByteCount();
      return; // Exit early, polling will handle the result
    }

    // Apply the result - only enable if audio is detected
    if (hasAudio) {
      this.enableMuteButton();
    }
    // If no audio detected, button stays disabled (already disabled by default)
    
    // Show video controls after audio detection is complete
    this.showVideoControls();
  }

  pollWebkitAudioDecodedByteCount() {
    let attempts = 0;
    const maxAttempts = 4; // Poll for up to 1 second (4 * 250ms)
    
    const checkWebkitAudio = () => {
      attempts += 1;
      
      if (this.player.webkitAudioDecodedByteCount > 0) {
        this.enableMuteButton();
        this.showVideoControls();
        return;
      }
      
      if (attempts < maxAttempts) {
        setTimeout(checkWebkitAudio, 250); // Check every 250ms
      } else {
        // After polling, button stays disabled (already disabled by default)
        this.showVideoControls(); // Still show controls even if no audio
      }
    };
    
    checkWebkitAudio();
  }

  enableMuteButton() {
    if (this.muteEl) {
      this.muteEl.disabled = false;
      this.muteEl.style.opacity = '1';
      this.muteEl.style.pointerEvents = 'auto';
      // Set proper icons and aria-label based on current mute state
      this.updateMuteButtonIcons();
    }
  }

  showVideoControls() {
    // Find the video controls container (parent of play and mute buttons)
    if (this.playEl && this.playEl.parentElement) {
      this.playEl.parentElement.classList.remove('opacity-0');
      this.playEl.parentElement.classList.add('opacity-100');
    } else if (this.muteEl && this.muteEl.parentElement) {
      this.muteEl.parentElement.classList.remove('opacity-0');
      this.muteEl.parentElement.classList.add('opacity-100');
    }
  }

  disableMuteButton() {
    if (this.muteEl) {
      this.muteEl.disabled = true;
      this.muteEl.style.opacity = '0.4';
      this.muteEl.style.pointerEvents = 'none';
      this.muteEl.setAttribute('aria-label', 'No audio available');
    }
  }

  bindEventListeners() {
    this.playEl?.addEventListener('click', this.handlePlay);
    this.muteEl?.addEventListener('click', this.handleMute);
    if (this.layout && this.hasMultipleVideos) {
      if (this.data.mobileFullHeight) {
        window.addEventListener('resize', () => {
          if (this.timerId) clearTimeout(this.timerId);
          this.timerId = setTimeout(() => {
            this.handleResize();
          }, 500);
        });
      } else {
        window.addEventListener('bpChange', this.handleBpChange);
      }
    }
  }
}
